import { UploadedFile } from 'components/Dropzone';
import { useAiAgent } from 'contexts/AiAgentContext';
import { useLayoutContext } from 'contexts/LayoutContext';
import { UploadType } from 'further-types/files';
import useFileUpload from 'hooks/ui/useFileUpload';
import api from 'lib/trpcClient';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatMessage, PendingInvoice } from '../types';
import { fileToBase64, getMediaType } from '../utils';
import { FileAttachment, ResponseSegmentType } from 'further-types/lp/ai-agent';

export const useChatMessages = () => {
  const { pendingNotification, clearNotification } = useAiAgent();
  const { selectedVehicleId } = useLayoutContext();
  const { uploadFile } = useFileUpload();
  const chatMutation = api.aiAgent.chat.useMutation();
  const sessionId = useRef(crypto.randomUUID());

  const [messages, setMessages] = useState<Array<ChatMessage>>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stagedFile, setStagedFile] = useState<File | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingInvoiceRef = useRef<PendingInvoice | null>(null);
  const refocusAfterLoadRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const snapToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    }, 50);
  }, []);

  useEffect(() => {
    if (!isLoading && refocusAfterLoadRef.current) {
      refocusAfterLoadRef.current = false;
      inputRef.current?.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    if (pendingNotification) {
      setMessages((prev) => [
        ...prev,
        { role: 'action', content: pendingNotification },
      ]);
      clearNotification();
      scrollToBottom();
    }
  }, [pendingNotification, clearNotification, scrollToBottom]);

  const assistantErrorMessage = (content: string): ChatMessage => ({
    role: 'assistant',
    content,
    segments: [{ type: ResponseSegmentType.Text, content }],
    pendingInvoice: null,
    timestamp: new Date(),
  });

  const sendMessage = async (text: string, fileOverride?: File) => {
    const currentStagedFile = fileOverride ?? stagedFile;
    if (!text.trim() && !currentStagedFile) return;
    if (!selectedVehicleId) return;
    if (isLoading) return;

    setIsLoading(true);

    let fileAttachment: FileAttachment | undefined;
    let displayFileName: string | undefined;

    if (currentStagedFile) {
      try {
        const mediaType = getMediaType(currentStagedFile);
        if (!mediaType) {
          setMessages((prev) => [
            ...prev,
            assistantErrorMessage(
              'Unsupported file type. Please upload a PDF.',
            ),
          ]);
          setIsLoading(false);
          return;
        }
        const uploadedFile = Object.assign(currentStagedFile, {
          path: currentStagedFile.name,
        }) as UploadedFile;

        const [base64, uploadResult] = await Promise.all([
          fileToBase64(currentStagedFile),
          uploadFile(uploadedFile, UploadType.LpTransactionAttachments),
        ]);

        if (!uploadResult.data) {
          setMessages((prev) => [
            ...prev,
            assistantErrorMessage(
              uploadResult.error?.message ?? 'Failed to upload the file.',
            ),
          ]);
          setIsLoading(false);
          return;
        }

        pendingInvoiceRef.current = {
          base64,
          mediaType,
          fileName: currentStagedFile.name,
          uploadedPath: uploadResult.data.filePath,
          uploadedName: uploadResult.data.name ?? currentStagedFile.name,
          uploadedPreview: uploadResult.data.url,
        };

        fileAttachment = {
          base64,
          mediaType,
          fileName: currentStagedFile.name,
        };
        displayFileName = currentStagedFile.name;
      } catch {
        setMessages((prev) => [
          ...prev,
          assistantErrorMessage(
            'Failed to process the file. Please try again.',
          ),
        ]);
        setIsLoading(false);
        return;
      }
    }

    const messageContent =
      text.trim() || (displayFileName ? 'Please process this file' : '');

    const userMessage: ChatMessage = {
      role: 'user',
      content: messageContent,
      fileName: displayFileName,
      timestamp: new Date(),
    };
    const newMessages = [...messages, userMessage];
    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setStagedFile(null);
    refocusAfterLoadRef.current = true;
    scrollToBottom();

    try {
      const apiMessages = newMessages
        .filter(
          (m): m is ChatMessage & { role: 'user' | 'assistant' } =>
            m.role === 'user' || m.role === 'assistant',
        )
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await chatMutation.mutateAsync({
        vehicleId: selectedVehicleId,
        messages: apiMessages,
        file: fileAttachment,
        sessionId: sessionId.current,
      });

      const hasNavigation = response.segments.some(
        (s) => s.type === ResponseSegmentType.Navigation,
      );
      const replyMessages: Array<ChatMessage> = [
        {
          role: 'assistant',
          content: response.message,
          segments: response.segments,
          pendingInvoice: hasNavigation ? pendingInvoiceRef.current : null,
          timestamp: new Date(),
        },
      ];
      if (hasNavigation) pendingInvoiceRef.current = null;

      setMessages((prev) => [...prev, ...replyMessages]);
      scrollToBottom();
    } catch (error) {
      console.error('Chat FE error', error);
      pendingInvoiceRef.current = null;
      setMessages((prev) => [
        ...prev,
        assistantErrorMessage('Sorry, something went wrong. Please try again.'),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const stageFile = (rawFile: File) => {
    if (isLoading) return;
    const mediaType = getMediaType(rawFile);
    if (!mediaType) {
      setMessages((prev) => [
        ...prev,
        assistantErrorMessage('Unsupported file type. Please upload a PDF.'),
      ]);
      return;
    }
    setStagedFile(rawFile);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading) return;
    const file = e.target.files?.[0];
    if (file) stageFile(file);
    e.target.value = '';
  };

  return {
    messages,
    inputText,
    setInputText,
    isLoading,
    stagedFile,
    setStagedFile,
    sendMessage,
    stageFile,
    handleFileInputChange,
    messagesEndRef,
    inputRef,
    fileInputRef,
    scrollToBottom,
    snapToBottom,
  };
};
